import { paths } from '@omnea-digital/file-storage-client'
import { GenericResponse } from '@omnea-digital/toolbox'
import createClient from 'openapi-fetch'
import { ServiceUrls } from '@omnea-app/packages/universal/general/serviceUrls'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import type { LoggerPort } from '@omnea-app/packages/universal/general/observability/LoggerPort'

const MAX_VALIDATION_WAIT_MS = 25_000
const INITIAL_BACKOFF_MS = 300
const MAX_BACKOFF_MS = 4_000

// The declared length is the server's claim, so the cap is enforced again per chunk against what
// actually arrives: reading the whole body first would materialise an oversize file before any check
// could refuse it. A null body is a null-body status (204), which is genuinely zero bytes.
const readCappedBody = async (
  body: ReadableStream<Uint8Array> | null,
  { filestoreId, maxBytes }: { filestoreId: string; maxBytes: number }
): Promise<Buffer> => {
  if (body === null) return Buffer.alloc(0)

  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let total = 0

  for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
    total += chunk.value.byteLength

    if (total > maxBytes) {
      await reader.cancel().catch(() => {})
      throw new FileTooLargeError({ filestoreId, sizeBytes: total, maxBytes })
    }

    chunks.push(chunk.value)
  }

  return Buffer.concat(chunks, total)
}

interface Params {
  authorization: string
  logger?: LoggerPort
}

type DownloadValidatedFileInput = {
  filestoreId: string
  maxBytes?: number
  // 'text' UTF-8-decodes the body; use 'base64' or 'buffer' for binary content (PDF/xlsx/zip) to avoid corruption.
  output?: 'text' | 'base64' | 'buffer'
  // Per-call validation-poll budget; defaults to MAX_VALIDATION_WAIT_MS (25s). Long-running callers
  // (e.g. the import-job worker) can extend it; synchronous API-Gateway callers keep the default.
  maxValidationWaitMs?: number
}

// Carries HTTP status as data (not templated into the message) so it stays searchable and callers can
// branch on status; a plain Error subclass keeps this FE-bundled module free of lambda-only toolbox errors.
export class FileStorageClientError extends Error {
  public readonly operation: 'upload' | 'download'
  public readonly status: number
  public readonly errorType: string
  public constructor(data: { operation: 'upload' | 'download'; status: number; errorType: string }) {
    super('File storage request failed')
    this.name = 'FileStorageClientError'
    this.operation = data.operation
    this.status = data.status
    this.errorType = data.errorType
  }
}

// Thrown when a download exceeds the caller's `maxBytes` — a permanent, caller-addressable condition,
// so backend callers can map it to a 4xx instead of a server fault. The message keeps the words
// "too large": callers that predate this class match `/too large/i` on the message.
export class FileTooLargeError extends Error {
  public readonly filestoreId: string
  public readonly sizeBytes: number
  public readonly maxBytes: number
  public constructor(data: { filestoreId: string; sizeBytes: number; maxBytes: number }) {
    super('File is too large for this operation')
    this.name = 'FileTooLargeError'
    this.filestoreId = data.filestoreId
    this.sizeBytes = data.sizeBytes
    this.maxBytes = data.maxBytes
  }
}

// Thrown only when the async antivirus scan is still pending after the poll budget — a genuinely
// retryable condition, distinct from a hard file-storage failure. Callers that surface a retry hint
// (e.g. a 409) should key off this type and treat every other error as a server fault.
export class FileValidationTimeoutError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'FileValidationTimeoutError'
  }
}

// Thrown when the /download request itself fails (non-2xx) while polling for validation. Carries the
// HTTP status as data so backend callers can map 403/404 (client faults) vs a 5xx.
export class FileStorageRequestError extends Error {
  public readonly status: number
  public readonly filestoreId: string
  public readonly attempt: number
  public readonly errorType: string
  public constructor(data: { status: number; filestoreId: string; attempt: number; errorType: string }) {
    super('Failed to download file from file storage')
    this.name = 'FileStorageRequestError'
    this.status = data.status
    this.filestoreId = data.filestoreId
    this.attempt = data.attempt
    this.errorType = data.errorType
  }
}

// Quarantine verdict on a /download 200 — OpenAPI only lists VALIDATED|PENDING, but the service returns these too.
export class FileValidationRejectedError extends Error {
  public readonly filestoreId: string
  public readonly validationStatus: 'QUARANTINED' | 'SOFT_QUARANTINED'
  public constructor(data: { filestoreId: string; validationStatus: 'QUARANTINED' | 'SOFT_QUARANTINED' }) {
    super(`File validation rejected with status ${data.validationStatus} (filestoreId: ${data.filestoreId})`)
    this.name = 'FileValidationRejectedError'
    this.filestoreId = data.filestoreId
    this.validationStatus = data.validationStatus
  }
}

export class FileStorageApiClient {
  POST
  GET
  private readonly logger?: LoggerPort

  constructor({ authorization, logger }: Params) {
    const { POST, GET } = createClient<paths>({
      baseUrl: ServiceUrls.FILE_STORAGE,
      headers: { Authorization: authorization },
    })
    this.POST = POST
    this.GET = GET
    this.logger = logger
  }

  // Throws (rather than returning) on a failed request or an empty slot list so a bad upload surfaces as
  // a 5xx instead of a silent success with no filestoreId.
  async uploadFile({
    name,
    content,
    contentType,
  }: {
    name: string
    // Buffer is a Uint8Array subclass, so this accepts Buffer callers too without a redundant union member.
    content: Uint8Array | string
    contentType: string
  }): Promise<{ filestoreId: string }> {
    // The /upload endpoint validates `type` against an exact-match MIME allowlist, so strip any
    // parameters (e.g. '; charset=utf-8') — the PUT below still carries the full contentType.
    const registeredType = contentType.split(';')[0]?.trim() ?? contentType
    const { data, error, response } = await this.POST('/upload', {
      body: { documents: [{ name, type: registeredType, uploadId: 0 }] },
    })
    if (error !== undefined) {
      throw new FileStorageClientError({ operation: 'upload', status: response.status, errorType: error.type })
    }
    const [slot] = data ?? []
    if (slot === undefined) {
      throw new Error('File storage upload returned no presigned slot')
    }

    const uploadResult = await fetch(slot.presignedPutUrl, {
      method: 'PUT',
      // DOM's BodyInit rejects ArrayBufferLike-backed views (e.g. Buffer) that undici accepts at runtime.
      // Cast rather than copy into a fresh Uint8Array — a copy would double peak memory on large exports.
      body: content as BodyInit,
      headers: { 'Content-Type': contentType },
    })
    if (!uploadResult.ok) {
      // Release the socket before throwing — S3 returns an XML error body that keeps the undici
      // connection tied up if left unconsumed, which leaks under concurrent or retried uploads.
      await uploadResult.body?.cancel().catch(() => {})
      throw new FileStorageClientError({ operation: 'upload', status: uploadResult.status, errorType: uploadResult.statusText })
    }

    return { filestoreId: slot.filestoreId }
  }

  // Returns the signed URL once the file is VALIDATED, or null while it isn't downloadable yet (the
  // application layer reads null as "not ready" → 404). Throws on a genuine API error so it surfaces as
  // a 5xx rather than falling through to that 404.
  async getSignedDownloadUrl({ filestoreId }: { filestoreId: string }): Promise<string | null> {
    const { data, error, response } = await this.POST('/download', { body: { filestoreId } })
    if (error !== undefined) {
      throw new FileStorageClientError({ operation: 'download', status: response.status, errorType: error.type })
    }
    return data?.validationStatus === 'VALIDATED' ? data.signedUrl : null
  }

  downloadValidatedFile(input: DownloadValidatedFileInput & { output: 'base64' }): Promise<{ data: string; mimeType: string }>
  downloadValidatedFile(input: DownloadValidatedFileInput & { output: 'buffer' }): Promise<{ data: Buffer; mimeType: string }>
  downloadValidatedFile(
    input: Omit<DownloadValidatedFileInput, 'output'> | (DownloadValidatedFileInput & { output?: 'text' })
  ): Promise<string>
  async downloadValidatedFile({
    filestoreId,
    maxBytes,
    output = 'text',
    maxValidationWaitMs,
  }: DownloadValidatedFileInput): Promise<string | { data: string | Buffer; mimeType: string }> {
    this.logger?.info('Downloading file from file storage', { filestoreId })

    const signedUrl = await this.waitForValidatedSignedUrl({ filestoreId, maxValidationWaitMs })

    const fileResponse = await fetch(signedUrl)
    if (!fileResponse.ok) {
      throw new Error(`Failed to fetch file from signed URL (filestoreId: ${filestoreId})`)
    }

    if (maxBytes !== undefined) {
      const contentLengthHeader = fileResponse.headers.get('content-length')
      const declaredSize = contentLengthHeader === null ? null : Number(contentLengthHeader)
      if (declaredSize !== null && (!Number.isFinite(declaredSize) || declaredSize > maxBytes)) {
        // Release the socket before throwing — an unconsumed body ties up the undici connection.
        await fileResponse.body?.cancel().catch(() => {})
        throw new FileTooLargeError({ filestoreId, sizeBytes: declaredSize, maxBytes })
      }
    }

    const bytes =
      maxBytes === undefined
        ? Buffer.from(await fileResponse.arrayBuffer())
        : await readCappedBody(fileResponse.body, { filestoreId, maxBytes })

    if (output === 'buffer' || output === 'base64') {
      const mimeType = fileResponse.headers.get('content-type') ?? 'application/octet-stream'
      return { data: output === 'base64' ? bytes.toString('base64') : bytes, mimeType }
    }

    return new TextDecoder('utf-8').decode(bytes)
  }

  // Polls until the file is VALIDATED, unlike getSignedDownloadUrl's single check, so callers that must
  // have the bytes can outwait an in-flight antivirus scan. A budget of 0 checks once without waiting,
  // so a caller out of time still learns whether the scan is running or has refused the file.
  async waitForValidatedSignedUrl({
    filestoreId,
    maxValidationWaitMs = MAX_VALIDATION_WAIT_MS,
  }: {
    filestoreId: string
    maxValidationWaitMs?: number
  }): Promise<string> {
    // Guard the loop bound: Infinity would poll forever, NaN and negatives are caller bugs.
    if (!Number.isFinite(maxValidationWaitMs) || maxValidationWaitMs < 0) {
      throw new Error(
        `maxValidationWaitMs must be a finite non-negative number (filestoreId: ${filestoreId}, got: ${maxValidationWaitMs})`
      )
    }

    let waited = 0
    let backoff = INITIAL_BACKOFF_MS
    let attempt = 0

    do {
      attempt++
      const { data, error, response } = await this.POST('/download', { body: { filestoreId } })

      if (error) {
        throw new FileStorageRequestError({ status: response.status, filestoreId, attempt, errorType: error.type })
      }

      const { validationStatus } = data
      switch (validationStatus) {
        case 'VALIDATED':
          return data.signedUrl
        case 'PENDING':
          break
        default: {
          // OpenAPI's 200 union omits quarantine statuses; they still arrive once a mid-poll scan finishes.
          const status = validationStatus as string
          if (status === 'QUARANTINED' || status === 'SOFT_QUARANTINED') {
            throw new FileValidationRejectedError({ filestoreId, validationStatus: status })
          }
          assertNever(validationStatus, this.logger)
          throw new Error(`File has unexpected validation status (filestoreId: ${filestoreId})`)
        }
      }

      const remaining = maxValidationWaitMs - waited
      const delay = Math.min(backoff, remaining)
      if (delay < 100) break

      this.logger?.info('File still validating, retrying', { filestoreId, attempt, delay })
      await new Promise((resolve) => setTimeout(resolve, delay))
      waited += delay
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS)
    } while (waited < maxValidationWaitMs)

    throw new FileValidationTimeoutError(
      `File validation timed out after ${maxValidationWaitMs / 1000}s (filestoreId: ${filestoreId}, attempts: ${attempt})`
    )
  }
}

export type FileStorageResponse<T extends keyof paths, T2 extends keyof paths[T]> = GenericResponse<paths, T, T2>

export type FileStorageUploadResponseItem = NonNullable<FileStorageResponse<'/upload', 'post'>['data']>[number]
